/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ICodeableConcept } from '@ahryman40k/ts-fhir-types/lib/R4';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { FhirRuleType, FhirWidgetBase } from '../algorithm/types';
import { applyRulesToFhirPatientData, DiagnosticReportAndObservationsBase, getLoincCode } from './fhir_utils';
import {
  fetchObservationsForDiagnosticReportThunk,
  FhirPatientDataType,
  selectIsLoadingFhirPatientData,
  selectPredefinedWidgetsMap,
  selectGroupedObservationsMap,
  fetchPredefinedWidgetsThunk,
  selectFhirDiagnosticReports,
  selectFhirCareEverywhere,
} from './fhirSlice';
import { IObservationWithPearlCode } from './fhir_types';
import { CareEverywherePanel } from './CareEverywhere/network';

export function getPearlCodeForObservation(code: ICodeableConcept) {
  return getLoincCode(code?.coding) || code?.coding?.[0]?.code || code?.text || '';
}

export function useFhirObserverations(
  predefinedWidget: FhirWidgetBase | undefined,
  onUpdate: (() => void) | undefined,
  fhirPatientData: FhirPatientDataType,
  careEverywhereData: CareEverywherePanel[] | undefined,
  showAll: boolean = false,
  filter?: string
): DiagnosticReportAndObservationsBase[] | undefined {
  if (showAll && predefinedWidget) throw new Error('showAll and predefinedWidget cannot be used together');
  const dispatch = useAppDispatch();
  const predefinedWidgetOptionsMap = useAppSelector(selectPredefinedWidgetsMap);
  const groupedObservationIdsByCptCode = useAppSelector(selectGroupedObservationsMap)?.groupedObservationIdsByCptCode;
  useEffect(() => {
    if (!predefinedWidgetOptionsMap) {
      const request = dispatch(fetchPredefinedWidgetsThunk());
      return () => request.abort();
    }
  }, [dispatch, predefinedWidgetOptionsMap]);
  const observations = useMemo(() => {
    if (predefinedWidget) {
      if (!predefinedWidget.rules) return undefined;
      const observations = applyRulesToFhirPatientData(
        fhirPatientData,
        careEverywhereData,
        predefinedWidget.rules,
        filter,
        groupedObservationIdsByCptCode
      );
      return observations;
    }
    // This is only if we are not using a predefined widget
    if (!showAll) return undefined;
    const rules = {
      type: 'DiagnosticReportName',
      name: '.*',
    } as FhirRuleType;
    const observations = applyRulesToFhirPatientData(fhirPatientData, careEverywhereData, rules, filter);
    return observations;
  }, [predefinedWidget, showAll, fhirPatientData, careEverywhereData, filter, groupedObservationIdsByCptCode]);

  useEffect(() => {
    onUpdate?.();
  }, [observations]); // eslint-disable-line react-hooks/exhaustive-deps
  return observations;
}

/**
 * Just a wrapper around selectFhirPatientData and selectIsLoadingFhirPatientData with a helpful callback to fetch observations
 */
function useFhirPatientData() {
  const loading = useAppSelector(selectIsLoadingFhirPatientData);
  const data = useAppSelector(selectFhirDiagnosticReports);
  const careEverywhereData = useAppSelector(selectFhirCareEverywhere);

  const dispatch = useAppDispatch();

  const loadPanelObservationsCb = useCallback(
    (panel: DiagnosticReportAndObservationsBase) => {
      const request = dispatch(
        fetchObservationsForDiagnosticReportThunk({ diagnostic_report_id: panel.diagnosticReport.id })
      );
      return Object.assign(request.unwrap(), { abort: () => request.abort() });
    },
    [dispatch]
  );

  return { loading, data, careEverywhereData, loadPanelObservationsCb };
}

const FETCH_ALL_PANEL_OBSERVATIONS = false;

type AbortablePromise<T> = Promise<T> & { abort: () => void };

export function useAutoloadObservations(
  loadingPanels: boolean,
  panelWithObservations: DiagnosticReportAndObservationsBase[] | undefined,
  disableAutoloadObservations: boolean | undefined,
  loadPanelObservationsCb: (panel: DiagnosticReportAndObservationsBase) => AbortablePromise<any>,
  historicalIndex: number | null // if set to non-null, the load only that panel, otherwise load all
) {
  const [loadingObservations, setLoadingObservations] = useState(false);
  // console.log('useAutoloadObservations', loadingPanels, panelWithObservations?.length, disableAutoloadObservations);
  useEffect(() => {
    if (!panelWithObservations || disableAutoloadObservations) return;
    let cancelled = false;
    let activeRequest: AbortablePromise<any> | undefined;
    setLoadingObservations(true);
    async function run() {
      for (let i = 0; panelWithObservations && i < panelWithObservations.length; i++) {
        const panel = panelWithObservations[i]!;
        if (
          FETCH_ALL_PANEL_OBSERVATIONS ||
          historicalIndex === null ||
          historicalIndex === i ||
          (!panel.isLoaded && panel.hideAllIfNoWhitelistMatches)
        ) {
          if (panel?.diagnosticReport.result?.[0]?.reference) {
            // We have something to load
            try {
              // console.log('Loading observations for panel', i, panelWithObservations);
              activeRequest = loadPanelObservationsCb(panel);
              await activeRequest;
              activeRequest = undefined;
            } catch (e) {
              if (!cancelled) {
                console.error(e);
              }
            }
          }
        }
      }
      if (!cancelled) {
        setLoadingObservations(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
      activeRequest?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [historicalIndex, loadingPanels, panelWithObservations?.length]);

  return loadingObservations;
}

export function useFhirPatientDataAndAutoloadObservations(
  predefinedWidget: FhirWidgetBase | undefined,
  // Used to update fhir sidebar sizes
  onUpdate: (() => void) | undefined,
  historicalIndex: number | null,
  showAll?: boolean,
  disableAutoloadObservations?: boolean,
  filter?: string
) {
  const { loading: loadingPanel, data, careEverywhereData, loadPanelObservationsCb } = useFhirPatientData(); // Like using the selectFhirPatientData
  let panelWithObservations = useFhirObserverations(
    showAll ? undefined : predefinedWidget,
    onUpdate,
    data,
    careEverywhereData,
    showAll,
    filter
  );

  if (loadingPanel && !panelWithObservations) panelWithObservations = [];
  const loadingObservations = useAutoloadObservations(
    loadingPanel,
    panelWithObservations,
    disableAutoloadObservations,
    loadPanelObservationsCb,
    historicalIndex
  );
  return { loadingPanel, loadingObservations, panelWithObservations, loadPanelObservationsCb };
}

export function useShouldHighlightObservationFunc(observationGroupIds: (string | undefined)[] | undefined) {
  const groupedObservationIdsByCptCode = useAppSelector(selectGroupedObservationsMap)?.groupedObservationIdsByCptCode;

  const shouldHighlightObservationFunc = useCallback(
    (o: IObservationWithPearlCode) => {
      // Do a many to many check. If any in groupedObservationIdsByCptCode match any in observationGroupIds, return true
      return !!observationGroupIds?.some(
        (observationGroupId) =>
          observationGroupId && groupedObservationIdsByCptCode?.[o.pearlCode]?.has(observationGroupId)
      );
    },
    [groupedObservationIdsByCptCode, observationGroupIds]
  );
  return { shouldHighlightObservationFunc };
}
